import { InvestmentTransactionDTO, TransactionCategory } from '@inversor-hub/types';

export type BucketKind = 'income' | 'deductible' | 'non-deductible' | 'separate' | 'uncategorized';

export type BucketId =
  | 'rental-income'
  | 'other-income'
  | 'financing-costs'
  | 'taxes-and-fees'
  | 'community-fees'
  | 'repairs-and-maintenance'
  | 'suministros'
  | 'insurance-premiums'
  | 'professional-services'
  | 'legal-defense'
  | 'rental-management'
  | 'bad-debt'
  | 'depreciation-property'
  | 'depreciation-furniture'
  | 'other-deductible-expenses'
  | 'loan-principal'
  | 'improvements-and-acquisition'
  | 'capital-gains'
  | 'non-deductible-expenses'
  | 'uncategorized';

export interface IrpfBucket {
  id: BucketId;
  label: string;
  subtitle: string;
  kind: BucketKind;
  tooltip?: string;
}

export const IRPF_BUCKETS: IrpfBucket[] = [
  {
    id: 'rental-income',
    label: 'Ingresos íntegros por arrendamiento',
    subtitle: 'Rentas cobradas del inquilino durante el ejercicio.',
    kind: 'income',
  },
  {
    id: 'other-income',
    label: 'Otros ingresos del alquiler',
    subtitle: 'Ingresos vinculados al alquiler distintos de la renta mensual.',
    kind: 'income',
  },
  {
    id: 'financing-costs',
    label: 'Intereses y gastos de financiación',
    subtitle: 'Intereses y gastos del préstamo vinculados al inmueble arrendado.',
    kind: 'deductible',
    tooltip:
      'No incluye la devolución del principal del préstamo. Si el banco agrupa la cuota, conviene desglosarla.',
  },
  {
    id: 'taxes-and-fees',
    label: 'Tributos y tasas',
    subtitle: 'IBI, tasa de basuras y otros tributos locales.',
    kind: 'deductible',
  },
  {
    id: 'community-fees',
    label: 'Gastos de comunidad',
    subtitle: 'Cuotas ordinarias y extraordinarias de la comunidad de propietarios.',
    kind: 'deductible',
  },
  {
    id: 'repairs-and-maintenance',
    label: 'Conservación y reparación',
    subtitle: 'Gastos para mantener el inmueble en uso, no mejoras.',
    kind: 'deductible',
  },
  {
    id: 'suministros',
    label: 'Suministros (a cargo del propietario)',
    subtitle: 'Luz, agua, gas o internet si los paga el propietario.',
    kind: 'deductible',
  },
  {
    id: 'insurance-premiums',
    label: 'Primas de seguro',
    subtitle: 'Seguro del hogar, de impago u otros seguros del inmueble.',
    kind: 'deductible',
  },
  {
    id: 'professional-services',
    label: 'Servicios profesionales',
    subtitle: 'Gestoría, asesoría fiscal, administración de fincas y servicios similares.',
    kind: 'deductible',
  },
  {
    id: 'legal-defense',
    label: 'Defensa jurídica',
    subtitle: 'Gastos de abogado o defensa jurídica vinculados al alquiler.',
    kind: 'deductible',
  },
  {
    id: 'rental-management',
    label: 'Gestión del alquiler',
    subtitle: 'Property management, gestión de cobros, comercialización o administración.',
    kind: 'deductible',
  },
  {
    id: 'bad-debt',
    label: 'Saldos de dudoso cobro',
    subtitle: 'Impagos del inquilino que deben revisarse antes de deducir.',
    kind: 'deductible',
    tooltip:
      'La deducibilidad requiere cumplir condiciones y haber declarado previamente el ingreso exigible.',
  },
  {
    id: 'depreciation-property',
    label: 'Amortización del inmueble',
    subtitle: 'Amortización fiscal del inmueble arrendado.',
    kind: 'deductible',
  },
  {
    id: 'depreciation-furniture',
    label: 'Amortización de mobiliario y enseres',
    subtitle: 'Amortización de muebles, electrodomésticos y otros bienes cedidos.',
    kind: 'deductible',
  },
  {
    id: 'other-deductible-expenses',
    label: 'Otros gastos necesarios',
    subtitle: 'Otros gastos necesarios para obtener los ingresos del alquiler.',
    kind: 'deductible',
    tooltip: 'Úsalo solo cuando no encaje en una categoría más específica.',
  },
  {
    id: 'loan-principal',
    label: 'Principal del préstamo',
    subtitle: 'Devolución de capital de hipoteca o préstamo.',
    kind: 'non-deductible',
    tooltip: 'La devolución del principal no es gasto deducible del alquiler.',
  },
  {
    id: 'improvements-and-acquisition',
    label: 'Mejoras, compra y adquisición',
    subtitle: 'Mejoras, reformas de valor, compra y gastos asociados a la adquisición.',
    kind: 'separate',
    tooltip:
      'No son gasto directo del alquiler. Pueden afectar al valor de adquisición, amortización o ganancia futura.',
  },
  {
    id: 'capital-gains',
    label: 'Venta o transmisión',
    subtitle: 'Venta del inmueble y gastos de transmisión.',
    kind: 'separate',
    tooltip: 'Debe revisarse aparte de los rendimientos del alquiler.',
  },
  {
    id: 'non-deductible-expenses',
    label: 'No deducible / revisar',
    subtitle: 'Gastos sin tratamiento fiscal claro para revisar antes de declarar.',
    kind: 'non-deductible',
  },
  {
    id: 'uncategorized',
    label: 'Revisar',
    subtitle: 'Transacciones que requieren clasificación o desglose manual.',
    kind: 'uncategorized',
  },
];

export const BUCKETS_BY_ID: Record<BucketId, IrpfBucket> = IRPF_BUCKETS.reduce(
  (acc, bucket) => {
    acc[bucket.id] = bucket;
    return acc;
  },
  {} as Record<BucketId, IrpfBucket>
);

export function mapCategoryToBucket(category: TransactionCategory): BucketId {
  switch (category) {
    case 'RENTAL_INCOME':
      return 'rental-income';
    case 'TENANT_RECHARGES_INCOME':
    case 'DEPOSIT_WITHHELD':
    case 'OTHER_INCOME':
      return 'other-income';
    case 'FINANCING_INTEREST':
      return 'financing-costs';
    case 'LOCAL_TAXES':
      return 'taxes-and-fees';
    case 'COMMUNITY_FEES':
      return 'community-fees';
    case 'REPAIRS_MAINTENANCE':
      return 'repairs-and-maintenance';
    case 'UTILITIES':
      return 'suministros';
    case 'INSURANCE':
      return 'insurance-premiums';
    case 'PROFESSIONAL_SERVICES':
    case 'CLEANING':
      return 'professional-services';
    case 'LEGAL_DEFENSE':
      return 'legal-defense';
    case 'RENTAL_MANAGEMENT':
      return 'rental-management';
    case 'BAD_DEBT':
      return 'bad-debt';
    case 'DEPRECIATION_PROPERTY':
      return 'depreciation-property';
    case 'DEPRECIATION_FURNITURE':
      return 'depreciation-furniture';
    case 'OTHER_DEDUCTIBLE_EXPENSE':
      return 'other-deductible-expenses';
    case 'LOAN_PRINCIPAL':
      return 'loan-principal';
    case 'IMPROVEMENT':
    case 'ACQUISITION_COSTS':
      return 'improvements-and-acquisition';
    case 'SALE_INCOME':
    case 'SALE_COSTS':
      return 'capital-gains';
    case 'NON_DEDUCTIBLE_EXPENSE':
      return 'non-deductible-expenses';
    case 'REVIEW':
      return 'uncategorized';
    default:
      return 'uncategorized';
  }
}

export interface BucketAggregate {
  bucket: IrpfBucket;
  total: number;
  transactions: InvestmentTransactionDTO[];
}

export interface FiscalAggregate {
  year: number;
  byBucket: Record<BucketId, BucketAggregate>;
  totals: {
    grossIncome: number;
    deductibleExpenses: number;
    organizationalBalance: number;
  };
  hasAnyTransactions: boolean;
  allUncategorized: boolean;
}

export function getTransactionYear(tx: InvestmentTransactionDTO): number {
  return new Date(tx.date).getUTCFullYear();
}

export function getAvailableYears(transactions: InvestmentTransactionDTO[]): number[] {
  const years = new Set<number>();
  for (const tx of transactions) {
    years.add(getTransactionYear(tx));
  }
  return Array.from(years).sort((a, b) => b - a);
}

export function pickDefaultYear(
  availableYears: number[],
  currentYear = new Date().getUTCFullYear()
): number | undefined {
  if (availableYears.length === 0) return undefined;
  const lastCompleted = currentYear - 1;
  if (availableYears.includes(lastCompleted)) return lastCompleted;
  return availableYears[0];
}

export function aggregateFiscal(
  transactions: InvestmentTransactionDTO[],
  year: number
): FiscalAggregate {
  const filtered = transactions.filter((tx) => getTransactionYear(tx) === year);

  const byBucket = IRPF_BUCKETS.reduce(
    (acc, bucket) => {
      acc[bucket.id] = { bucket, total: 0, transactions: [] };
      return acc;
    },
    {} as Record<BucketId, BucketAggregate>
  );

  for (const tx of filtered) {
    const lines = tx.lines.length > 0 ? tx.lines : [{ category: tx.category, amount: tx.amount }];
    for (const line of lines) {
      const bucketId = mapCategoryToBucket(line.category);
      byBucket[bucketId].total += line.amount;
      byBucket[bucketId].transactions.push({
        ...tx,
        category: line.category,
        amount: line.amount,
      });
    }
  }

  let grossIncome = 0;
  let deductibleExpenses = 0;
  for (const entry of Object.values(byBucket)) {
    if (entry.bucket.kind === 'income') grossIncome += entry.total;
    else if (entry.bucket.kind === 'deductible') deductibleExpenses += entry.total;
  }

  const allUncategorized =
    filtered.length > 0 &&
    filtered.every((tx) => {
      const lines = tx.lines.length > 0 ? tx.lines : [{ category: tx.category, amount: tx.amount }];
      return lines.every((line) => mapCategoryToBucket(line.category) === 'uncategorized');
    });

  return {
    year,
    byBucket,
    totals: {
      grossIncome,
      deductibleExpenses,
      organizationalBalance: grossIncome - deductibleExpenses,
    },
    hasAnyTransactions: filtered.length > 0,
    allUncategorized,
  };
}
